import React, { useState, useMemo, Fragment } from 'react'
import {
  Column,
  ExpandedState,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table'
import Checkbox from '@mui/material/Checkbox'
import {
  TrainingSession,
  ActivityTypes,
  ACTIVITY_TYPE_LABELS,
  filterOptions,
  EditableInterval,
} from './utils'
import TrainingSessionIntervals from '../TrainingIntervalsTable'
import MultipleSelectChip from '../../../components/MultiSelectDropDown'

export type EditableSession = TrainingSession & {
  tempId: string
  isNew?: boolean
}

interface IProps {
  sessions: TrainingSession[]
  isEditMode?: boolean
  editedSessions?: EditableSession[]
  onSessionChange?: (tempId: string, field: string, value: string) => void
  onSessionBlur?: () => void
  onDeleteSessionRequest?: (tempId: string) => void
  planDateMin?: string
  planDateMax?: string
  editedIntervals?: Record<string, EditableInterval[]>
  unitSystem?: string | null
  onIntervalChange?: (
    sessionTempId: string,
    intervalTempId: string,
    field: string,
    value: unknown
  ) => void
  onIntervalBlur?: () => void
  onAddInterval?: (sessionTempId: string) => void
  onDeleteInterval?: (sessionTempId: string, intervalTempId: string) => void
  onDuplicateInterval?: (sessionTempId: string, intervalTempId: string) => void
  onReorderIntervals?: (
    sessionTempId: string,
    fromIndex: number,
    toIndex: number
  ) => void
}

export default function TrainingSessionsTable({
  sessions,
  isEditMode = false,
  editedSessions = [],
  onSessionChange,
  onSessionBlur,
  onDeleteSessionRequest,
  planDateMin,
  planDateMax,
  editedIntervals,
  unitSystem,
  onIntervalChange,
  onIntervalBlur,
  onAddInterval,
  onDeleteInterval,
  onDuplicateInterval,
  onReorderIntervals,
}: IProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'date', desc: false },
  ])
  const [columnVisibility, setColumnVisibility] = useState({
    id: true,
    title: true,
    date: true,
    day: false,
    week: false,
    description: false,
    embedUrl: false,
    completed: true,
    sportType: false,
    activityType: true,
    phase: false,
    templateOrder: false,
    status: true,
    createdAt: false,
    updatedAt: false,
  })

  const tableData: EditableSession[] = useMemo(() => {
    if (isEditMode) return editedSessions
    return sessions.map((s) => ({ ...s, tempId: String(s.id) }))
  }, [isEditMode, editedSessions, sessions])

  const activityTypeOptions = Object.values(ActivityTypes).filter(
    (t) =>
      t !== ActivityTypes.race &&
      t !== ActivityTypes.mainRace &&
      t !== ActivityTypes.strengthVideo &&
      t !== ActivityTypes.strengthGifs
  )

  const columns = React.useMemo<ColumnDef<EditableSession>[]>(
    () => [
      {
        accessorKey: 'id',
        enableColumnFilter: true,
        header: () => 'ID',
        filterFn: 'includesString',
        cell: ({ row, getValue }) => {
          return (
            <>
              {row?.getCanExpand() && (
                <button
                  onClick={() => row.toggleExpanded()}
                  style={{ cursor: 'pointer' }}
                  type="button"
                >
                  {row.getIsExpanded() ? '👇' : '👉'}
                </button>
              )}
              &nbsp;
              <span
                className={row.original.isNew ? 'text-gray-400 italic' : ''}
              >
                {row.original.isNew ? 'NEW' : (getValue() as string)}
              </span>
            </>
          )
        },
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'title',
        header: () => 'Title',
        footer: (props) => props.column.id,
        cell: ({ row }) => {
          if (isEditMode) {
            return (
              <input
                type="text"
                value={row.original.title}
                onChange={(e) =>
                  onSessionChange?.(
                    row.original.tempId,
                    'title',
                    e.target.value
                  )
                }
                onBlur={onSessionBlur}
                className="w-40 border border-blue-300 rounded px-2 py-1 text-xs bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )
          }
          return (
            <span className="text-blue-600 font-medium">
              {row.original.title}
            </span>
          )
        },
      },
      {
        accessorKey: 'date',
        header: () => 'Date',
        footer: (props) => props.column.id,
        cell: ({ row }) => {
          if (isEditMode) {
            const dateVal = row.original.date
              ? String(row.original.date).substring(0, 10)
              : ''
            return (
              <input
                type="date"
                value={dateVal}
                min={planDateMin}
                max={planDateMax}
                onChange={(e) =>
                  onSessionChange?.(row.original.tempId, 'date', e.target.value)
                }
                onBlur={onSessionBlur}
                className="border border-blue-300 rounded px-2 py-1 text-xs bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )
          }
          return (
            <span className="text-blue-600">
              {row.original.date
                ? String(row.original.date).substring(0, 10)
                : ''}
            </span>
          )
        },
      },
      {
        accessorKey: 'day',
        header: () => 'Day',
        footer: (props) => props.column.id,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'week',
        header: () => 'Week',
        footer: (props) => props.column.id,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'description',
        header: () => 'Description',
        footer: (props) => props.column.id,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'embedUrl',
        header: () => 'Embed Url',
        footer: (props) => props.column.id,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'completed',
        header: () => 'Completed',
        footer: (props) => props.column.id,
        cell: ({ row }) => {
          return row?.original?.completed ? '✅' : '❌'
        },
        filterFn: 'equals',
      },
      {
        accessorKey: 'sportType',
        header: () => 'Sport Type',
        footer: (props) => props.column.id,
        filterFn: 'arrIncludesSome',
      },
      {
        accessorKey: 'activityType',
        header: () => 'Activity Type',
        footer: (props) => props.column.id,
        filterFn: 'arrIncludesSome',
        cell: ({ row }) => {
          const currentType = row.original.activityType ?? ''
          if (isEditMode) {
            if (
              currentType === ActivityTypes.race ||
              currentType === ActivityTypes.mainRace
            ) {
              return (
                <span className="px-2 py-1 text-xs text-gray-500 italic">
                  {ACTIVITY_TYPE_LABELS[currentType] ?? currentType}
                </span>
              )
            }
            return (
              <select
                value={currentType}
                onChange={(e) =>
                  onSessionChange?.(
                    row.original.tempId,
                    'activityType',
                    e.target.value
                  )
                }
                className="border border-blue-300 rounded px-2 py-1 text-xs bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                {activityTypeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {ACTIVITY_TYPE_LABELS[opt] ?? opt}
                  </option>
                ))}
              </select>
            )
          }
          return ACTIVITY_TYPE_LABELS[currentType] ?? currentType
        },
      },
      {
        accessorKey: 'phase',
        header: () => 'Phase',
        footer: (props) => props.column.id,
        filterFn: 'arrIncludesSome',
      },
      {
        accessorKey: 'templateOrder',
        header: () => 'Template Order',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'status',
        header: () => 'Status',
        footer: (props) => props.column.id,
        filterFn: 'arrIncludesSome',
      },
      {
        accessorKey: 'createdAt',
        header: () => 'Created At',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'updatedAt',
        header: () => 'Updated At',
        footer: (props) => props.column.id,
      },
      ...(isEditMode
        ? [
            {
              id: '_delete',
              header: () => '',
              enableSorting: false,
              enableColumnFilter: false,
              // eslint-disable-next-line react/no-unused-prop-types
              cell: ({ row }: { row: any }) => (
                <button
                  type="button"
                  onClick={() => onDeleteSessionRequest?.(row.original.tempId)}
                  className="text-red-500 hover:text-red-700 text-lg leading-none transition-colors"
                  title="Delete session"
                >
                  🗑
                </button>
              ),
            } as ColumnDef<EditableSession>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditMode, onSessionChange, onDeleteSessionRequest]
  )

  const [expanded, setExpanded] = React.useState<ExpandedState>({})

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      expanded,
      pagination: {
        pageSize: 500,
        pageIndex: 0,
      },
      sorting,
      columnVisibility,
    },
    initialState: {
      columnFilters: isEditMode
        ? []
        : [{ id: 'status', value: ['active', 'taper'] }],
      sorting: [{ id: 'date', desc: false }],
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: (row) => {
      if (isEditMode) return true
      return !!row?.original?.intervals?.length
    },

    debugTable: process.env.NODE_ENV === 'development',
  })

  return (
    <div className="p-2">
      <div className="h-2" />

      <div className="inline-block border border-black shadow rounded mx-auto">
        <div className="px-1 border-b border-black">
          <label htmlFor="all">
            <input
              id="all"
              type="checkbox"
              checked={table.getIsAllColumnsVisible()}
              onChange={table.getToggleAllColumnsVisibilityHandler()}
            />{' '}
            Toggle All
          </label>
        </div>
        {table.getAllLeafColumns().map((column) => {
          if (column.id === '_delete') return null
          return (
            <div key={column.id} className="px-1">
              <label htmlFor={column?.id}>
                <input
                  id={column.id}
                  type="checkbox"
                  checked={column.getIsVisible()}
                  onChange={column.getToggleVisibilityHandler()}
                />{' '}
                {column.id}
              </label>
            </div>
          )
        })}
      </div>
      <div className="mt-8">
        <div className="overflow-x-auto">
          <div className="py-2">
            <table className="w-full table-auto">
              <thead className="bg-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900"
                        >
                          {header.isPlaceholder ? null : (
                            <div>
                              {/* eslint-disable-next-line */}
                              <div
                                className={
                                  header.column.getCanSort()
                                    ? 'flex justify-center cursor-pointer select-none'
                                    : 'flex justify-center'
                                }
                                onClick={() =>
                                  header.column.getToggleSortingHandler()
                                }
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: ' 🔼',
                                  desc: ' 🔽',
                                }[header.column.getIsSorted() as string] ??
                                  null}
                              </div>

                              {header.column.getCanFilter() ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </div>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <Fragment key={row?.original?.tempId ?? row?.id}>
                      <tr key={row.id} className="border-t border-gray-200">
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className="bg-gray-50 py-2 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-3"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          )
                        })}
                      </tr>
                      {row.getIsExpanded() && (
                        <tr>
                          <td colSpan={columns.length}>
                            <TrainingSessionIntervals
                              intervals={row?.original?.intervals ?? []}
                              isEditMode={isEditMode}
                              sessionTempId={row.original.tempId}
                              editedIntervals={
                                editedIntervals?.[row.original.tempId]
                              }
                              unitSystem={unitSystem}
                              onIntervalChange={onIntervalChange}
                              onIntervalBlur={onIntervalBlur}
                              onAddInterval={onAddInterval}
                              onDeleteInterval={onDeleteInterval}
                              onDuplicateInterval={onDuplicateInterval}
                              onReorderIntervals={onReorderIntervals}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="mt-4">{table.getRowModel().rows.length} Rows</div>
    </div>
  )
}

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue()

  const sortedUniqueValues = useMemo(() => {
    return filterOptions[column?.id as unknown as string] || []
  }, [column?.id])

  if (column?.id === 'completed') {
    return (
      <Checkbox
        checked={!!columnFilterValue}
        onChange={(e) => column.setFilterValue(e.target.checked)}
      />
    )
  }
  return sortedUniqueValues?.length ? (
    <div>
      <MultipleSelectChip
        data={
          sortedUniqueValues?.map((v: string) => ({ key: v, title: v })) ?? []
        }
        selectedData={
          (column.getFilterValue() as string[])?.map((v) => ({
            key: v,
            title: v,
          })) ?? []
        }
        label={column?.id}
        onChange={({ key }) => {
          const existingFilter = (column.getFilterValue() as string[]) || []
          const newFilterState = existingFilter?.includes(key as string)
            ? existingFilter?.filter((v) => v !== key)
            : [...existingFilter, key]
          column.setFilterValue(newFilterState)
        }}
      />
      <div className="h-1" />
    </div>
  ) : (
    <>
      <input
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={(e) => {
          column.setFilterValue(e.target.value)
        }}
        placeholder="Search..."
        className="w-36 border shadow rounded"
      />
      <div className="h-1" />
    </>
  )
}
